/**
 * Copy via a throwaway textarea + `document.execCommand`. Deprecated, but it
 * is the only thing that works outside a secure context.
 */
const legacyCopy = (text: string): boolean => {
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    // Off-screen but still selectable — `display: none` would break the copy.
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);

    const selection = document.getSelection();
    const previousRange =
      selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

    textarea.select();
    textarea.setSelectionRange(0, text.length);
    const copied = document.execCommand("copy");

    document.body.removeChild(textarea);
    if (selection && previousRange) {
      selection.removeAllRanges();
      selection.addRange(previousRange);
    }

    return copied;
  } catch {
    return false;
  }
};

/**
 * Copy `text` to the clipboard.
 *
 * `navigator.clipboard` only exists in a secure context (HTTPS or localhost),
 * so on a plain-HTTP portal it is `undefined` and calling `writeText` on it
 * throws. It can also reject when the document isn't focused or the
 * permission is denied. Both cases fall back to the legacy textarea trick.
 *
 * @returns true only when the text actually reached the clipboard, so callers
 * never show a success toast for a copy that didn't happen.
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (!text) return false;

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Blocked or unavailable — fall through.
    }
  }

  return legacyCopy(text);
};
